type ScrollLockSnapshot = {
  scrollX: number;
  scrollY: number;
  bodyStyleAttribute: string | null;
  htmlStyleAttribute: string | null;
  bodyHadFreezeClass: boolean;
};

type InteractionLockSnapshot = {
  appRoot: HTMLElement;
  ariaHidden: string | null;
  inert: boolean;
  focusedElement: HTMLElement | null;
};

let scrollLockCount = 0;
let interactionLockCount = 0;
let scrollLockSnapshot: ScrollLockSnapshot | null = null;
let interactionLockSnapshot: InteractionLockSnapshot | null = null;
let scrollLockSyncQueued = false;
let bodyStyleObserver: MutationObserver | null = null;

const restoreStyleAttribute = (
  element: HTMLElement,
  styleAttribute: string | null
) => {
  if (styleAttribute === null) {
    element.removeAttribute("style");
  } else {
    element.setAttribute("style", styleAttribute);
  }
};

const isBodyScrollLockActive = () => {
  return (
    document.body.classList.contains("freeze") &&
    document.body.style.position === "fixed" &&
    document.documentElement.style.overflow === "hidden"
  );
};

const applyBodyScrollLock = () => {
  if (!scrollLockSnapshot) return;

  // The building-block modal clears the complete body style attribute during
  // cleanup. Restore the pre-lock styles before reapplying an active lock.
  if (document.body.getAttribute("style") === "") {
    restoreStyleAttribute(
      document.body,
      scrollLockSnapshot.bodyStyleAttribute
    );
  }

  document.body.classList.add("freeze");
  document.documentElement.style.overflow = "hidden";
  document.documentElement.style.overscrollBehavior = "none";
  document.body.style.position = "fixed";
  document.body.style.top = `-${scrollLockSnapshot.scrollY}px`;
  document.body.style.left = `-${scrollLockSnapshot.scrollX}px`;
  document.body.style.right = "0";
  document.body.style.width = "100%";
  document.body.style.overflow = "hidden";
};

const restoreBodyScrollLock = () => {
  if (!scrollLockSnapshot) return;

  const snapshot = scrollLockSnapshot;
  scrollLockSnapshot = null;

  restoreStyleAttribute(document.body, snapshot.bodyStyleAttribute);
  restoreStyleAttribute(document.documentElement, snapshot.htmlStyleAttribute);
  if (!snapshot.bodyHadFreezeClass) {
    document.body.classList.remove("freeze");
  }
  window.scrollTo(snapshot.scrollX, snapshot.scrollY);
};

const ensureBodyScrollGuard = () => {
  if (typeof MutationObserver === "undefined" || bodyStyleObserver) return;

  bodyStyleObserver = new MutationObserver(() => {
    if (scrollLockCount <= 0 || !scrollLockSnapshot) return;
    if (!isBodyScrollLockActive() || document.body.getAttribute("style") === "") {
      applyBodyScrollLock();
    }
  });

  bodyStyleObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ["style", "class"]
  });
  bodyStyleObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["style"]
  });
};

const teardownBodyScrollGuard = () => {
  bodyStyleObserver?.disconnect();
  bodyStyleObserver = null;
};

const scheduleBodyScrollSync = () => {
  if (scrollLockSyncQueued) return;
  scrollLockSyncQueued = true;

  Promise.resolve().then(() => {
    scrollLockSyncQueued = false;
    if (scrollLockCount > 0) {
      ensureBodyScrollGuard();
      applyBodyScrollLock();
    } else {
      teardownBodyScrollGuard();
      restoreBodyScrollLock();
    }
  });
};

export const acquireBodyScrollLock = () => {
  if (typeof document === "undefined") return;

  scrollLockCount += 1;
  if (!scrollLockSnapshot) {
    const body = document.body;
    const html = document.documentElement;

    scrollLockSnapshot = {
      scrollX: window.scrollX,
      scrollY: window.scrollY,
      bodyStyleAttribute: body.getAttribute("style"),
      htmlStyleAttribute: html.getAttribute("style"),
      bodyHadFreezeClass: body.classList.contains("freeze")
    };
  }

  ensureBodyScrollGuard();
  applyBodyScrollLock();
  scheduleBodyScrollSync();
};

export const releaseBodyScrollLock = () => {
  if (typeof document === "undefined" || scrollLockCount === 0) return;

  scrollLockCount -= 1;
  scheduleBodyScrollSync();
};

export const acquireBackgroundInteractionLock = () => {
  if (typeof document === "undefined") return;

  if (interactionLockCount > 0) {
    interactionLockCount += 1;
    return;
  }

  const appRoot = document.getElementById("__next");
  if (!appRoot) return;

  interactionLockSnapshot = {
    appRoot,
    ariaHidden: appRoot.getAttribute("aria-hidden"),
    inert: appRoot.inert,
    focusedElement:
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null
  };

  appRoot.inert = true;
  appRoot.setAttribute("aria-hidden", "true");
  interactionLockCount = 1;
};

export const releaseBackgroundInteractionLock = () => {
  if (typeof document === "undefined" || interactionLockCount === 0) return;

  interactionLockCount -= 1;
  if (interactionLockCount > 0 || !interactionLockSnapshot) return;

  const snapshot = interactionLockSnapshot;
  interactionLockSnapshot = null;
  snapshot.appRoot.inert = snapshot.inert;

  if (snapshot.ariaHidden === null) {
    snapshot.appRoot.removeAttribute("aria-hidden");
  } else {
    snapshot.appRoot.setAttribute("aria-hidden", snapshot.ariaHidden);
  }

  if (snapshot.focusedElement?.isConnected) {
    snapshot.focusedElement.focus({ preventScroll: true });
  }
};
